import { useState, useCallback } from "react";
import { X, Search, Users } from "react-feather";
import { debounce } from "lodash";
import { useAuth } from "../../../global-components/src";
import { api } from "../../../services/api";
import { getImageUser, getImageUserNotFound } from "../../../global-components/src/utils/auth";
import { PACOCA_API_URL } from "../../../global-components/src/utils/vars";
import { User } from "../../../global-components/src/types";
import "./style.css";
import { Member } from "../../../pages/ChatMenu/GroupInfoPanel";
import { useTranslation } from "react-i18next";

interface Props {
    show: boolean;
    onClose: () => void;
    onCreated: (chatId: number, groupName: string) => void;
    /** When set, skip the name step and add selected users to an existing group */
    addMembersToGroup?: number;
    onMembersAdded?: (added: Member[]) => void;
}

const CreateGroupModal = ({ show, onClose, onCreated, addMembersToGroup, onMembersAdded }: Props) => {
    const { token } = useAuth();
    const { t } = useTranslation();
    const [step, setStep] = useState<"select" | "name">("select");
    const [search, setSearch] = useState("");
    const [results, setResults] = useState<User[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
    const [loadingSearch, setLoadingSearch] = useState(false);
    const [groupName, setGroupName] = useState("");
    const [creating, setCreating] = useState(false);

    const doSearch = useCallback(
        debounce(async (q: string) => {
            if (!q.trim()) { setResults([]); return; }
            setLoadingSearch(true);
            try {
                const res = await api(token).get(`${PACOCA_API_URL}/search`, {
                    params: { page: 0, typeSeach: "user", search: q }
                });
                setResults(res.data?.users ?? res.data ?? []);
            } catch {
                setResults([]);
            } finally {
                setLoadingSearch(false);
            }
        }, 400),
        [token]
    );

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
        doSearch(e.target.value);
    };

    const toggleUser = (u: User) => {
        setSelectedUsers(prev =>
            prev.find(s => s.id === u.id)
                ? prev.filter(s => s.id !== u.id)
                : [...prev, u]
        );
    };

    const handleAddMembers = async () => {
        if (!addMembersToGroup || selectedUsers.length === 0) return;
        setCreating(true);
        try {
            await api(token).post(`${PACOCA_API_URL}/chats/group/${addMembersToGroup}/add-members`, {
                user_ids: selectedUsers.map(u => u.id),
            });
            if (onMembersAdded) onMembersAdded(selectedUsers as any);
            handleClose();
        } catch {
            // silent
        } finally {
            setCreating(false);
        }
    };

    const handleCreate = async () => {
        if (addMembersToGroup) { handleAddMembers(); return; }
        if (!groupName.trim() || selectedUsers.length === 0) return;
        setCreating(true);
        try {
            const res = await api(token).post(`${PACOCA_API_URL}/chats/group`, {
                group_name: groupName.trim(),
                user_ids: selectedUsers.map(u => u.id),
            });
            onCreated(res.data.chat_id, res.data.group_name);
            handleClose();
        } catch {
            // silent – error handling delegated to caller if needed
        } finally {
            setCreating(false);
        }
    };

    const handleClose = () => {
        setStep("select");
        setSearch("");
        setResults([]);
        setSelectedUsers([]);
        setGroupName("");
        onClose();
    };

    if (!show) return null;

    return (
        <div className="create-group-overlay" onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}>
            <div className="create-group-modal">
                <div className="create-group-header">
                    <button className="create-group-close" type="button" onClick={handleClose}>
                        <X size={18} />
                    </button>
                    <h5 className="create-group-title">
                        {step === "select" ? t("chat.newConversation") : t("chat.group.editName")}
                    </h5>
                    {step === "select" ? (
                        <button
                            className="create-group-next"
                            disabled={selectedUsers.length === 0 || creating}
                            type="button"
                            onClick={() => addMembersToGroup ? handleCreate() : setStep("name")}
                        >
                            {addMembersToGroup ? (creating ? t("chat.group.adding") : t("chat.group.add")) : t("chat.group.advance")}
                        </button>
                    ) : (
                        <button
                            className="create-group-next"
                            disabled={!groupName.trim() || creating}
                            type="button"
                            onClick={handleCreate}
                        >
                            {creating ? t("chat.group.creating") : t("chat.group.create")}
                        </button>
                    )}
                </div>

                {step === "select" ? (
                    <>
                        {selectedUsers.length > 0 && (
                            <div className="create-group-chips">
                                {selectedUsers.map(u => (
                                    <span key={u.id} className="create-group-chip">
                                        <img
                                            src={getImageUser(u.img_account)}
                                            onError={(e) => { e.currentTarget.src = getImageUserNotFound(u.name); }}
                                        />
                                        {u.name.split(" ")[0]}
                                        <button type="button" onClick={() => toggleUser(u)}><X size={11} /></button>
                                    </span>
                                ))}
                            </div>
                        )}

                        <div className="create-group-search-wrap">
                            <Search size={15} className="create-group-search-icon" />
                            <input
                                autoFocus
                                className="create-group-search"
                                placeholder={t("generics.searchPeople")}
                                value={search}
                                onChange={handleSearch}
                            />
                        </div>

                        <div className="create-group-results">
                            {loadingSearch ? (
                                <div className="create-group-loading">
                                    <span className="placeholder" style={{ width: "80%", height: 10, display: "block", marginBottom: 8 }} />
                                    <span className="placeholder" style={{ width: "60%", height: 10, display: "block" }} />
                                </div>
                            ) : results.length === 0 && search.trim() ? (
                                <p className="create-group-empty">{t("chat.noUsersFound")}</p>
                            ) : results.length === 0 && !search.trim() ? (
                                <p className="create-group-empty" style={{ marginTop: 32 }}>
                                    <Users size={28} style={{ display: "block", margin: "0 auto 8px" }} />
                                    {t("chat.group.searchToAdd")}
                                </p>
                            ) : (
                                results.map((u: User) => {
                                    const selected = !!selectedUsers.find(s => s.id === u.id);
                                    return (
                                        <div
                                            key={u.id}
                                            className={`create-group-user-row ${selected ? "selected" : ""}`}
                                            onClick={() => toggleUser(u)}
                                        >
                                            <img
                                                className="create-group-user-img"
                                                src={getImageUser(u.img_account)}
                                                onError={(e) => { e.currentTarget.src = getImageUserNotFound(u.name); }}
                                            />
                                            <div className="create-group-user-info">
                                                <span className="create-group-user-name">{u.name}</span>
                                                <span className="create-group-user-sub">@{u.user_name}</span>
                                            </div>
                                            <div className={`create-group-check ${selected ? "on" : ""}`} />
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </>
                ) : (
                    <div className="create-group-name-step">
                        <div className="create-group-avatar-placeholder">
                            <Users size={36} />
                        </div>
                        <input
                            autoFocus
                            className="create-group-name-input"
                            placeholder={t("chat.group.panelTitle")}
                            maxLength={100}
                            value={groupName}
                            onChange={e => setGroupName(e.target.value)}
                            onKeyDown={e => { if (e.key === "Enter") handleCreate(); }}
                        />
                        <p className="create-group-name-hint">
                            {t("chat.group.member_one", { count: selectedUsers.length })}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CreateGroupModal;
